import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, LoginCredentials, UserRole, SignupWithOTPInput } from '../types';
import authService from '../services/auth.service';

// ============================================================================
// AUTH CONTEXT TYPES
// ============================================================================

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithOTP: (email: string, otp: string) => Promise<void>;
  signupWithOTP: (input: SignupWithOTPInput) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isAdminOrAbove: boolean;
  isHR: boolean;
  canAccessHRFeatures: boolean;
  isAgent: boolean;
  isDealer: boolean;
  isClient: boolean;
  isEmployee: boolean;
  canAccessClient: (clientId: string) => boolean;
  canModify: boolean;
}

// ============================================================================
// AUTH CONTEXT
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// AUTH PROVIDER
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (authService.isAuthenticated()) {
          // Try to get stored user first
          const storedUser = authService.getStoredUser();
          if (storedUser) {
            setUser(storedUser);
          }

          // Then validate with server
          try {
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
          } catch {
            // Token might be invalid, clear auth
            authService.clearAuth();
            setUser(null);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Login handler (password-based)
  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const response = await authService.login(credentials);
      setUser(response.user);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Login with OTP handler
  const loginWithOTP = useCallback(async (email: string, otp: string) => {
    setIsLoading(true);
    try {
      const response = await authService.loginWithOTP(email, otp);
      setUser(response.user);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Signup with OTP handler
  const signupWithOTP = useCallback(async (input: SignupWithOTPInput) => {
    setIsLoading(true);
    try {
      const response = await authService.signupWithOTP(input);
      setUser(response.user);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout handler
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch {
      // If refresh fails, logout
      await logout();
    }
  }, [logout]);

  // Role checks - use roles array for multi-role support
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const isSuperAdmin = userRoles.includes(UserRole.SUPER_ADMIN);
  const isAdmin = userRoles.includes(UserRole.ADMIN);
  const isAdminOrAbove = isSuperAdmin || isAdmin;
  const isHR = userRoles.includes(UserRole.HR);
  const canAccessHRFeatures = isSuperAdmin || isAdmin || isHR;
  const isAgent = userRoles.includes(UserRole.AGENT);
  const isDealer = userRoles.includes(UserRole.DEALER);
  const isClient = userRoles.includes(UserRole.CLIENT);
  const isEmployee = userRoles.includes(UserRole.EMPLOYEE);

  // Check if user can access a specific client's data
  const canAccessClient = useCallback(
    (clientId: string): boolean => {
      if (!user) return false;
      if (isAdminOrAbove) return true;
      if (isClient) return user.clientId === clientId;
      // Dealers see all clients (no per-user client scoping).
      if (isDealer) return true;
      if (isAgent) return user.assignedClientIds?.includes(clientId) || false;
      return false;
    },
    [user, isAdminOrAbove, isClient, isAgent, isDealer]
  );

  // Anyone in the trading-desk scope can create/close trades.
  const canModify = isAdminOrAbove || isAgent || isDealer;

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    loginWithOTP,
    signupWithOTP,
    logout,
    refreshUser,
    isSuperAdmin,
    isAdmin,
    isAdminOrAbove,
    isHR,
    canAccessHRFeatures,
    isAgent,
    isDealer,
    isClient,
    isEmployee,
    canAccessClient,
    canModify,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// USE AUTH HOOK
// ============================================================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
